import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MDSpinner from "react-md-spinner";
import {
  FaChevronDown,
  FaChevronRight,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaGasPump,
  FaLeaf,
  FaMapMarkedAlt,
  FaTachometerAlt,
  FaWrench,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { usePendingActions } from "../../contexts/PendingActionsContext";
import {
  acknowledgePendingAction,
  updateSessionFinishReadings,
} from "./fieldAppApi";
import {
  METER_BOTH,
  METER_HOUR,
  METER_ODOMETER,
  normalizeMeterType,
  resolvePanelReadingValue,
} from "./fieldAppMeterLabels";
import {
  filterPendingByCategory,
  PENDING_ACTION_CATEGORIES,
  resolvePendingCategory,
} from "./pendingActionCategories";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatLocation(location) {
  if (!location || location.latitude == null || location.longitude == null) {
    return "—";
  }
  return `${Number(location.latitude).toFixed(6)}, ${Number(location.longitude).toFixed(6)}`;
}

function farmLabel(item) {
  return item.farmName || (item.farmId ? String(item.farmId) : "");
}

function anomalyKindLabel(kind, t) {
  const labels = {
    regression: t("Leitura abaixo da anterior"),
    excessive_jump: t("Salto grande na leitura"),
    excessive_rate: t("Horímetro acima do tempo de trabalho"),
  };
  return labels[kind] || kind || t("Inconsistência");
}

function meterAnomalyMeterLabel(meter, t) {
  if (meter === "odometer") {
    return t("Hodômetro");
  }
  if (meter === "hour_meter") {
    return t("Horímetro");
  }
  return t("Medidor");
}

function meterAnomalyUnit(meter) {
  return meter === "odometer" ? "km" : "h";
}

function formatMeterDelta(value, unit) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  const num = Number(value);
  const formatted = formatNumber(Math.abs(num));
  if (num > 0) {
    return `+${formatted} ${unit}`;
  }
  if (num < 0) {
    return `−${formatted} ${unit}`;
  }
  return `0 ${unit}`;
}

function meterTypesForItem(item) {
  const meterType = normalizeMeterType(item.meterType);
  if (meterType === METER_BOTH) {
    return [METER_HOUR, METER_ODOMETER];
  }
  if (meterType === METER_ODOMETER) {
    return [METER_ODOMETER];
  }
  return [METER_HOUR];
}

function coalesce(...values) {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value != null) {
      return value;
    }
  }
  return null;
}

function resolveAssumeReading(item, session, meter, anomalies) {
  const fromSession = sessionAssumeReading(session, meter);
  if (fromSession != null) {
    return fromSession;
  }
  const anomaly = (anomalies || []).find((row) => row && row.meter === meter);
  if (anomaly && anomaly.context !== "finish_below_session_start") {
    if (item.eventType === "ASSUMIR_MAQUINA") {
      return coalesce(anomaly.current, anomaly.reference, null);
    }
    return coalesce(anomaly.reference, null);
  }
  if (item.eventType === "ASSUMIR_MAQUINA") {
    return meter === METER_ODOMETER ? item.odometer : item.hourMeter;
  }
  return null;
}

function resolveFinishReading(item, session, meter, anomalies) {
  const fromSession = sessionFinishReading(session, meter);
  if (fromSession != null) {
    return fromSession;
  }
  const anomaly = (anomalies || []).find((row) => row && row.meter === meter);
  if (anomaly) {
    if (anomaly.context === "finish_below_session_start") {
      return coalesce(anomaly.current, null);
    }
    if (item.eventType === "FINALIZAR_TRABALHO") {
      return coalesce(anomaly.current, null);
    }
    return coalesce(anomaly.current, null);
  }
  if (item.eventType === "FINALIZAR_TRABALHO") {
    return meter === METER_ODOMETER ? item.odometer : item.hourMeter;
  }
  return null;
}

function sessionAssumeReading(session, meter) {
  if (!session) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? session.assumeOdometer
    : session.assumeHourMeter;
}

function sessionFinishReading(session, meter) {
  if (!session) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? session.finishOdometer
    : session.finishHourMeter;
}

function latestSystemReadingValue(latest, meter) {
  if (!latest) {
    return null;
  }
  return meter === METER_ODOMETER || meter === "odometer"
    ? latest.odometer
    : latest.hourMeter;
}

function buildMeterAnomalyTableRows(item) {
  const session = item.sessionReadings || {};
  const latest = item.latestSystemReading || {};
  const anomaliesByMeter = (item.anomalies || []).reduce((acc, anomaly) => {
    if (!anomaly || !anomaly.meter) {
      return acc;
    }
    if (!acc[anomaly.meter]) {
      acc[anomaly.meter] = [];
    }
    acc[anomaly.meter].push(anomaly);
    return acc;
  }, {});

  return meterTypesForItem(item)
    .map((meter) => {
      const meterAnomalies = anomaliesByMeter[meter] || [];
      const assume = resolveAssumeReading(
        item,
        session,
        meter,
        meterAnomalies.length ? meterAnomalies : item.anomalies
      );
      const finish = resolveFinishReading(
        item,
        session,
        meter,
        meterAnomalies.length ? meterAnomalies : item.anomalies
      );
      const systemLatest = latestSystemReadingValue(latest, meter);
      const sessionDelta =
        finish != null && assume != null ? finish - assume : null;

      return {
        key: meter,
        meter,
        assume,
        finish,
        systemLatest,
        sessionDelta,
        anomalies: meterAnomalies.length
          ? meterAnomalies
          : (item.anomalies || []).filter(
              (row) => !row || !row.meter || row.meter === meter
            ),
      };
    })
    .filter(
      (row) =>
        row.anomalies.length > 0 ||
        row.assume != null ||
        row.finish != null ||
        row.systemLatest != null
    );
}

function AcknowledgeAlertButton({ item, t, onResolved, className }) {
  const [saving, setSaving] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const handleAcknowledge = async () => {
    if (!item.recordUuid) {
      return;
    }
    setSaving(true);
    try {
      await acknowledgePendingAction({
        recordUuid: item.recordUuid,
        actionType: item.actionType,
        managerNote: noteDraft.trim() || undefined,
      });
      toast.success(t("Alerta reconhecido e removido da lista."));
      setNoteOpen(false);
      setNoteDraft("");
      if (onResolved) {
        await onResolved();
      }
    } catch (error) {
      toast.error(
        t("Não foi possível reconhecer o alerta. Tente novamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className || "field-app-pending-action-actions"}>
      {noteOpen ? (
        <div className="field-app-pending-action-form">
          <label htmlFor={`ack-note-${item.recordUuid}`}>
            {t("Observação (opcional)")}
          </label>
          <textarea
            id={`ack-note-${item.recordUuid}`}
            rows={2}
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder={t("Ex.: conferido com operador; leitura corrigida no painel.")}
          />
        </div>
      ) : null}
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={saving}
        onClick={() => {
          if (noteOpen) {
            handleAcknowledge();
            return;
          }
          setNoteOpen(true);
        }}
      >
        {noteOpen ? t("Confirmar reconhecimento") : t("Reconhecer / dispensar")}
      </button>
      {noteOpen ? (
        <button
          type="button"
          className="btn btn-sm btn-default"
          disabled={saving}
          onClick={() => {
            setNoteOpen(false);
            setNoteDraft("");
          }}
        >
          {t("Cancelar")}
        </button>
      ) : null}
    </div>
  );
}

function MeterAnomalyActionCard({ item, t, onOpenInTracking, onResolved }) {
  const tableRows = buildMeterAnomalyTableRows(item);

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--meter-anomaly">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--meter-anomaly">
            <FaExclamationTriangle aria-hidden />
            {t(item.actionLabel || "Leitura inconsistente de odômetro ou horímetro")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Contexto")}: {item.eventContextLabel || item.eventType || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--meter-anomaly">
          {t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {t(
          "Compare leituras ao assumir e ao sair da máquina com a última leitura registrada no sistema e confira o motivo de cada alerta."
        )}
      </p>

      {tableRows.length > 0 ? (
        <div className="field-app-pending-action-meter-table-wrap">
          <table className="field-app-pending-action-meter-table">
            <thead>
              <tr>
                <th>{t("Medidor")}</th>
                <th>{t("Leitura ao assumir")}</th>
                <th>{t("Leitura ao sair")}</th>
                <th>{t("Última leitura no sistema")}</th>
                <th>{t("Percorrido na sessão")}</th>
                <th>{t("Motivo")}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => {
                const unit = meterAnomalyUnit(row.meter);
                const deltaClass =
                  row.sessionDelta != null && Number(row.sessionDelta) < 0
                    ? "field-app-pending-action-meter-delta--negative"
                    : row.sessionDelta != null && Number(row.sessionDelta) > 0
                      ? "field-app-pending-action-meter-delta--positive"
                      : "";

                return (
                  <tr key={row.key}>
                    <td>{meterAnomalyMeterLabel(row.meter, t)}</td>
                    <td>
                      {row.assume != null ? (
                        <span className="field-app-pending-action-meter-value">
                          {formatNumber(row.assume)} {unit}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {row.finish != null ? (
                        <span className="field-app-pending-action-meter-value field-app-pending-action-meter-value--current">
                          {formatNumber(row.finish)} {unit}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {row.systemLatest != null ? (
                        <span className="field-app-pending-action-meter-value">
                          {formatNumber(row.systemLatest)} {unit}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={deltaClass}>
                      {formatMeterDelta(row.sessionDelta, unit)}
                    </td>
                    <td>
                      {row.anomalies.length > 0 ? (
                        row.anomalies.map((anomaly, index) => (
                          <div key={`${anomaly.kind}-${index}`}>
                            <strong>{anomalyKindLabel(anomaly.kind, t)}</strong>
                            {anomaly.message ? (
                              <span className="field-app-pending-action-meter-detail">
                                {anomaly.message}
                              </span>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="field-app-pending-action-readings">
          {item.hourMeter != null ? (
            <div>
              <strong>{t("Horímetro informado")}</strong>
              <span>{formatNumber(item.hourMeter)} h</span>
            </div>
          ) : null}
          {item.odometer != null ? (
            <div>
              <strong>{t("Hodômetro informado")}</strong>
              <span>{formatNumber(item.odometer)} km</span>
            </div>
          ) : null}
        </div>
      )}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking && item.sessionUuid ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function fuelAnomalyKindLabel(kind, t) {
  const labels = {
    tank_over_capacity: t("Litros acima da capacidade do tanque"),
    tank_full_mismatch: t(
      "Tanque cheio inconsistente com abastecimentos do período"
    ),
    fuel_without_meter_progress: t(
      "Abastecimento sem avanço no medidor (consumo sem horas/km registradas)"
    ),
    consumption_above_max: t("Consumo acima do máximo cadastrado"),
    consumption_below_min: t("Consumo abaixo do mínimo cadastrado"),
    consumption_above_max_kpl: t("Rendimento acima do máximo cadastrado (km/L)"),
    consumption_below_min_kpl: t("Rendimento abaixo do mínimo cadastrado (km/L)"),
    regression: t("Horímetro abaixo da leitura anterior"),
    excessive_jump: t("Salto grande no horímetro"),
    excessive_rate: t("Horímetro acima do tempo de trabalho"),
  };
  return labels[kind] || anomalyKindLabel(kind, t);
}

function FuelSupplyAnomalyActionCard({ item, t, onResolved }) {
  const anomalies = item.anomalies || [];

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--fuel">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--fuel">
            <FaGasPump aria-hidden />
            {t(item.actionLabel || "Abastecimento inconsistente")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--fuel">
          {t("Alerta")}
        </span>
      </header>

      <div className="field-app-pending-action-readings">
        <div>
          <strong>{t("Litros abastecidos")}</strong>
          <span>{formatNumber(item.liters)} L</span>
        </div>
        <div>
          <strong>
            {item.meterType === "odometer" ? t("Hodômetro") : t("Horímetro")}
          </strong>
          <span>
            {formatNumber(item.hourMeter)}{" "}
            {item.meterType === "odometer" ? "km" : "h"}
          </span>
        </div>
        {item.tankCapacity != null ? (
          <div>
            <strong>{t("Capacidade do tanque")}</strong>
            <span>{formatNumber(item.tankCapacity)} L</span>
          </div>
        ) : null}
        {item.consumptionMinLph != null || item.consumptionMaxLph != null ? (
          <div>
            <strong>{t("Faixa de consumo cadastrada")}</strong>
            <span>
              {formatNumber(item.consumptionMinLph)} – {formatNumber(item.consumptionMaxLph)} L/h
            </span>
          </div>
        ) : null}
        {item.consumptionMinKpl != null || item.consumptionMaxKpl != null ? (
          <div>
            <strong>{t("Faixa de rendimento cadastrada")}</strong>
            <span>
              {formatNumber(item.consumptionMinKpl)} – {formatNumber(item.consumptionMaxKpl)} km/L
            </span>
          </div>
        ) : null}
      </div>

      {anomalies.length > 0 ? (
        <ul className="field-app-pending-action-anomalies">
          {anomalies.map((anomaly, index) => (
            <li key={`${anomaly.kind}-${index}`}>
              <strong>{fuelAnomalyKindLabel(anomaly.kind, t)}</strong>
              {anomaly.message ? `: ${anomaly.message}` : null}
            </li>
          ))}
        </ul>
      ) : null}

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function RemoteFinishActionCard({
  item,
  t,
  onResolved,
  onOpenInTracking,
  onEditingChange,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [hourDraft, setHourDraft] = useState("");
  const [odometerDraft, setOdometerDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [acceptNoteDraft, setAcceptNoteDraft] = useState("");
  const [acceptNoteOpen, setAcceptNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!onEditingChange) {
      return undefined;
    }
    const isEditing = editorOpen || acceptNoteOpen;
    if (!isEditing) {
      return undefined;
    }
    onEditingChange(true);
    return () => onEditingChange(false);
  }, [acceptNoteOpen, editorOpen, onEditingChange]);

  const acceptWithoutData = async () => {
    if (!item.recordUuid) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await acknowledgePendingAction({
        recordUuid: item.recordUuid,
        actionType: item.actionType,
        managerNote: acceptNoteDraft.trim() || undefined,
        acceptWithoutData: true,
      });
      toast.success(
        t("Encerramento aceito sem incluir horímetro ou hodômetro.")
      );
      setAcceptNoteOpen(false);
      setAcceptNoteDraft("");
      setEditorOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(
        t("Não foi possível aceitar o encerramento. Tente novamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  const saveClosure = async () => {
    const hourMeter =
      hourDraft.trim() === ""
        ? null
        : Number.parseFloat(String(hourDraft).replace(",", "."));
    const odometer =
      odometerDraft.trim() === ""
        ? null
        : Number.parseFloat(String(odometerDraft).replace(",", "."));

    if (hourMeter == null && odometer == null) {
      setError(
        t("Informe horímetro e/ou hodômetro final, ou abra no rastreamento.")
      );
      return;
    }

    if (!item.sessionUuid) {
      setError(t("Sessão não identificada para este registro."));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateSessionFinishReadings(item.farmId, {
        sessionUuid: item.sessionUuid,
        hourMeter,
        odometer,
        managerNote: noteDraft.trim() || null,
      });
      toast.success(t("Encerramento salvo."));
      setEditorOpen(false);
      if (onResolved) {
        await onResolved();
      }
    } catch (err) {
      setError(t("Não foi possível salvar o encerramento."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--remote">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type">
            <FaExclamationTriangle aria-hidden />
            {t(item.actionLabel || "Esqueci de finalizar a máquina")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge">{t("Pendente")}</span>
      </header>

      {item.observations ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Observação do operador")}:</strong> {item.observations}
        </p>
      ) : null}

      {item.finishObservationAudioUrl ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Áudio do operador")}:</strong>{" "}
          <audio controls preload="none" src={item.finishObservationAudioUrl}>
            {t("Seu navegador não reproduz áudio.")}
          </audio>
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        <strong>{t("Local informado")}:</strong> {formatLocation(item.location)}
      </p>

      <p className="field-app-pending-action-hint">
        {t(
          "O operador pode assumir esta máquina normalmente no dia seguinte. Esta pendência não bloqueia novo trabalho — complete o encerramento quando puder (mesmo dias depois)."
        )}
      </p>

      <div className="field-app-pending-action-actions">
        <button
          type="button"
          className="btn btn-sm btn-warning"
          onClick={() => setEditorOpen((current) => !current)}
          disabled={saving}
        >
          {editorOpen ? t("Fechar editor") : t("Completar encerramento")}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default field-app-pending-action-accept-without-data"
          disabled={saving || acceptNoteOpen}
          onClick={() => setAcceptNoteOpen(true)}
        >
          <FaCheckCircle aria-hidden style={{ marginRight: 6 }} />
          {t("Aceitar encerramento sem incluir dados")}
        </button>
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
            disabled={saving}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Abrir no rastreamento")}
          </button>
        ) : null}
      </div>

      {acceptNoteOpen ? (
        <div className="field-app-pending-action-form field-app-pending-action-form--accept">
          <p className="field-app-pending-action-hint">
            {t(
              "O encerramento será removido da lista sem informar horímetro ou hodômetro final."
            )}
          </p>
          <label>
            {t("Anotação do gestor (opcional)")}
            <textarea
              rows={2}
              value={acceptNoteDraft}
              disabled={saving}
              onChange={(event) => setAcceptNoteDraft(event.target.value)}
              placeholder={t(
                "Ex.: operador confirmou que a máquina já estava encerrada no campo."
              )}
            />
          </label>
          <div className="field-app-pending-action-form-actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={saving}
              onClick={acceptWithoutData}
            >
              {saving ? t("Salvando…") : t("Confirmar aceite")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-default"
              disabled={saving}
              onClick={() => {
                setAcceptNoteOpen(false);
                setAcceptNoteDraft("");
              }}
            >
              {t("Cancelar")}
            </button>
          </div>
        </div>
      ) : null}

      {editorOpen ? (
        <div className="field-app-pending-action-form">
          <label>
            {t("Horímetro final (h)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={hourDraft}
              disabled={saving}
              onChange={(event) => setHourDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Hodômetro final (km)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={odometerDraft}
              disabled={saving}
              onChange={(event) => setOdometerDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Anotação do gestor (opcional)")}
            <textarea
              rows={2}
              value={noteDraft}
              disabled={saving}
              onChange={(event) => setNoteDraft(event.target.value)}
            />
          </label>
          {error ? (
            <p className="field-app-pending-action-error">{error}</p>
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving}
            onClick={saveClosure}
          >
            {saving ? t("Salvando…") : t("Salvar encerramento")}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function staleMeterDefectSuffix(staleMeters, t) {
  if (!staleMeters || staleMeters.length === 0) {
    return "";
  }
  return ` ${t("Pode indicar defeito no painel ou sensor.")}`;
}

function staleMeterReadingPair(item, meterKind) {
  const meterType =
    meterKind === "odometer" ? METER_ODOMETER : METER_HOUR;
  const startField =
    meterKind === "odometer" ? "assumeOdometer" : "assumeHourMeter";
  const finishField =
    meterKind === "odometer" ? "finishOdometer" : "finishHourMeter";
  const start =
    item[startField] != null
      ? item[startField]
      : resolvePanelReadingValue(
          {
            hourMeter: item.assumeHourMeter,
            odometer: item.assumeOdometer,
          },
          meterType
        );
  const finish =
    item[finishField] != null
      ? item[finishField]
      : resolvePanelReadingValue(
          {
            hourMeter: item.finishHourMeter,
            odometer: item.finishOdometer,
          },
          meterType
        );
  return { start, finish };
}

function StaleMeterActionCard({ item, t, onOpenInTracking, onResolved }) {
  const staleLabels = item.staleMeterLabels || [];
  const labelText =
    staleLabels.length > 0
      ? staleLabels.join(" e ")
      : t("horímetro/hodômetro");
  const defectSuffix = staleMeterDefectSuffix(item.staleMeters, t);
  const hourReadings = staleMeterReadingPair(item, "hour_meter");
  const odometerReadings = staleMeterReadingPair(item, "odometer");

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--meter">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--meter">
            <FaWrench aria-hidden />
            {t(item.actionLabel || "Odômetro ou horímetro sem variação no trabalho")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
            {item.machineStaleCount > 0 ? (
              <span>
                {t("Ocorrências na máquina")}: {item.machineStaleCount}
              </span>
            ) : null}
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--meter">
          {t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {item.pathDistanceM != null && item.pathDistanceM >= 100
          ? `${t(
              "O operador percorreu cerca de {{distance}} m no campo, mas informou a mesma leitura de {{meters}} ao assumir e ao finalizar",
              {
                distance: Math.round(item.pathDistanceM),
                meters: labelText,
              }
            )}${defectSuffix}`
          : `${t(
              "O operador informou a mesma leitura de {{meters}} ao assumir e ao finalizar o trabalho",
              { meters: labelText }
            )}${defectSuffix}`}
      </p>

      <div className="field-app-pending-action-readings">
        {(item.staleMeters || []).includes("hour_meter") ? (
          <div>
            <strong>{t("Horímetro")}</strong>
            <span>
              {t("Início")}: {formatNumber(hourReadings.start)} h → {t("Fim")}:{" "}
              {formatNumber(hourReadings.finish)} h
            </span>
          </div>
        ) : null}
        {(item.staleMeters || []).includes("odometer") ? (
          <div>
            <strong>{t("Hodômetro")}</strong>
            <span>
              {t("Início")}: {formatNumber(odometerReadings.start)} km → {t("Fim")}:{" "}
              {formatNumber(odometerReadings.finish)} km
            </span>
          </div>
        ) : null}
      </div>

      {item.machineMeterProblem ? (
        <p className="field-app-pending-action-problem">
          <FaWrench aria-hidden />
          {t(
            "Esta máquina foi marcada com possível problema no medidor. Horas, quilometragem e manutenções podem estar comprometidos — verifique o painel ou o sensor."
          )}
        </p>
      ) : (
        <p className="field-app-pending-action-hint">
          {t(
            "Máquinas paradas (<100 m) só geram alerta após 3 trabalhos seguidos com leitura igual. Com deslocamento no campo, o alerta é imediato."
          )}
        </p>
      )}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function SpeedViolationActionCard({ item, t, onOpenInTracking, onResolved }) {
  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--speed">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--speed">
            <FaTachometerAlt aria-hidden />
            {t(item.actionLabel || "Velocidade acima do limite")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--speed">
          {item.inProgress ? t("Em andamento") : t("Alerta")}
        </span>
      </header>

      <p className="field-app-pending-action-note">
        {item.inProgress
          ? t(
              "Trabalho ainda aberto. A máquina ultrapassou {{limit}} km/h (pico {{peak}} km/h) e já ficou {{duration}} acima do limite — cerca de {{percent}}% do tempo desde que assumiu a máquina ({{sessionDuration}}).",
              {
                limit: formatNumber(item.maxSpeedKmh),
                peak: formatNumber(item.peakSpeedKmh),
                duration:
                  item.secondsAboveLabel || formatNumber(item.secondsAboveLimit),
                percent: formatNumber(item.percentAboveLimit),
                sessionDuration:
                  item.sessionDurationLabel || formatNumber(item.sessionSeconds),
              }
            )
          : t(
              "A máquina ultrapassou a velocidade máxima configurada ({{limit}} km/h), atingindo {{peak}} km/h. Ficou {{duration}} acima do limite — cerca de {{percent}}% do tempo total do trabalho ({{sessionDuration}}).",
              {
                limit: formatNumber(item.maxSpeedKmh),
                peak: formatNumber(item.peakSpeedKmh),
                duration:
                  item.secondsAboveLabel || formatNumber(item.secondsAboveLimit),
                percent: formatNumber(item.percentAboveLimit),
                sessionDuration:
                  item.sessionDurationLabel || formatNumber(item.sessionSeconds),
              }
            )}
      </p>

      {item.whatsappEnabled ? (
        <p className="field-app-pending-action-hint">
          {t(
            "WhatsApp enviado ao gestor cadastrado (serviço pode gerar custo extra)."
          )}
        </p>
      ) : null}

      <div className="field-app-pending-action-actions">
        {onOpenInTracking ? (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenInTracking(item)}
          >
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Ver trabalho no rastreamento")}
          </button>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function HarvestLossExceededActionCard({ item, farmId, t, onResolved }) {
  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--harvest-loss">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--harvest-loss">
            <FaLeaf aria-hidden />
            {t(item.actionLabel || "Perda de colheita acima do limite")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || t("Amostra de perda")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Operador")}: {item.operatorName || "—"}
            </span>
            <span>
              {t("Data")}: {formatDateTime(item.finishedAt)}
            </span>
            {item.serviceOrderId ? (
              <span>
                {t("Ordem de serviço")}: #{item.serviceOrderId}
              </span>
            ) : null}
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--harvest-loss">
          {t("Alerta")}
        </span>
      </header>

      {item.serviceOrderTitle ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Serviço")}:</strong> {item.serviceOrderTitle}
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        {t(
          "Amostra com {{kg}} kg/ha ({{bags}} sc/ha), acima do limite de alerta de {{limit}} kg/ha. Margem tolerada da ordem: {{tolerated}} kg/ha (+{{percent}}%).",
          {
            kg: formatNumber(item.kgPerHa),
            bags: formatNumber(item.bagsPerHa),
            limit: formatNumber(item.alertLimitKgHa),
            tolerated: formatNumber(item.toleratedKgHa),
            percent: formatNumber(item.alertThresholdPercent),
          }
        )}
      </p>

      {item.seedCount != null ? (
        <p className="field-app-pending-action-note">
          <strong>{t("Sementes contadas")}:</strong>{" "}
          {formatNumber(item.seedCount)}
        </p>
      ) : null}

      <p className="field-app-pending-action-note">
        <strong>{t("Local da amostra")}:</strong> {formatLocation(item.location)}
      </p>

      <div className="field-app-pending-action-actions">
        {item.serviceOrderId ? (
          <Link
            to={`/${item.farmId || farmId}/pending-schedules/?obs=${item.serviceOrderId}&expand_loss=1`}
            className="btn btn-sm btn-default"
          >
            <FaClipboardList style={{ marginRight: 6 }} />
            {t("Abrir ordem de serviço")}
          </Link>
        ) : null}
      </div>

      <AcknowledgeAlertButton item={item} t={t} onResolved={onResolved} />
    </article>
  );
}

function MaintenanceOverdueActionCard({ item, t }) {
  const maintenanceLabel =
    item.maintenanceTitle || item.maintenanceName || "—";
  const nextDue =
    item.nextDueHours != null ? item.nextDueHours : item.targetHourMeter;
  const editHref = item.farmId
    ? `/${item.farmId}/field-app/?tab=maintenance`
    : null;

  return (
    <article className="field-app-pending-action-card field-app-pending-action-card--maintenance">
      <header className="field-app-pending-action-card-head">
        <div>
          <span className="field-app-pending-action-type field-app-pending-action-type--maintenance">
            <FaWrench aria-hidden />
            {t(item.actionLabel || "Manutenção vencida")}
          </span>
          <h3 className="field-app-pending-action-title">
            {item.machineName || item.machineIdent || t("Máquina")}
          </h3>
          <p className="field-app-pending-action-meta">
            {farmLabel(item) ? (
              <span>
                {t("Fazenda")}: {farmLabel(item)}
              </span>
            ) : null}
            <span>
              {t("Tarefa")}: {maintenanceLabel}
            </span>
            <span>
              {t("Próxima revisão")}: {formatNumber(nextDue)} h
            </span>
            <span>
              {t("Horímetro atual")}: {formatNumber(item.currentHourMeter)} h
            </span>
          </p>
        </div>
        <span className="field-app-pending-action-badge field-app-pending-action-badge--maintenance">
          {t("Vencida")}
        </span>
      </header>

      {item.hoursOverdue != null ? (
        <p className="field-app-pending-action-note">
          {t("A máquina está {{hours}} h acima do horímetro programado para esta manutenção.", {
            hours: formatNumber(item.hoursOverdue),
          })}
        </p>
      ) : (
        <p className="field-app-pending-action-note">
          {t(
            "O horímetro atual da máquina atingiu ou ultrapassou o valor programado para a próxima revisão."
          )}
        </p>
      )}

      {editHref ? (
        <div className="field-app-pending-action-actions">
          <Link className="btn btn-sm btn-primary" to={editHref}>
            {t("Ver agendamento")}
          </Link>
        </div>
      ) : null}
    </article>
  );
}

function PendingActionCard(props) {
  if (props.item.actionType === "maintenance_overdue") {
    return <MaintenanceOverdueActionCard {...props} />;
  }
  if (props.item.actionType === "meter_reading_anomaly") {
    return <MeterAnomalyActionCard {...props} />;
  }
  if (props.item.actionType === "fuel_supply_anomaly") {
    return <FuelSupplyAnomalyActionCard {...props} />;
  }
  if (props.item.actionType === "stale_meter_reading") {
    return <StaleMeterActionCard {...props} />;
  }
  if (props.item.actionType === "speed_violation") {
    return <SpeedViolationActionCard {...props} />;
  }
  if (props.item.actionType === "harvest_loss_exceeded") {
    return <HarvestLossExceededActionCard {...props} />;
  }
  return <RemoteFinishActionCard {...props} />;
}

function PendingActionCategorySection({
  category,
  items,
  farmId,
  t,
  onResolved,
  onOpenInTracking,
  onEditingChange,
  defaultExpanded,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!items.length) {
    return null;
  }

  return (
    <section className="field-app-pending-actions-group">
      <button
        type="button"
        className="field-app-pending-actions-group-head"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <span className="field-app-pending-actions-group-icon">
          {expanded ? <FaChevronDown /> : <FaChevronRight />}
        </span>
        <span className="field-app-pending-actions-group-title">{t(category.label)}</span>
        <span className="field-app-pending-actions-group-count">{items.length}</span>
      </button>
      {expanded ? (
        <div className="field-app-pending-actions-group-body">
          {items.map((item) => (
            <PendingActionCard
              key={`${item.actionType}-${item.sessionUuid || item.recordUuid || item.maintenanceId}`}
              item={item}
              farmId={farmId}
              t={t}
              onResolved={onResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={onEditingChange}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function FieldAppPendingActionsPanel({
  farmId,
  t,
  onOpenInTracking,
  alertCategory = "all",
}) {
  const location = useLocation();
  const {
    pendingItems,
    loading,
    refreshPendingActions,
    setPendingActionsEditing,
  } = usePendingActions();
  const [localLoading, setLocalLoading] = useState(false);

  const handleEditingChange = useCallback(
    (isEditing) => {
      setPendingActionsEditing(isEditing);
    },
    [setPendingActionsEditing]
  );

  const activeCategory = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get("alert");
    return fromUrl || alertCategory || "all";
  }, [alertCategory, location.search]);

  const reload = useCallback(async () => {
    setLocalLoading(true);
    try {
      await refreshPendingActions();
    } finally {
      setLocalLoading(false);
    }
  }, [refreshPendingActions]);

  useEffect(() => {
    reload();
  }, [farmId, reload]);

  const handleResolved = useCallback(async () => {
    await refreshPendingActions({ background: true });
  }, [refreshPendingActions]);

  const visibleItems = useMemo(
    () => filterPendingByCategory(pendingItems, activeCategory, farmId),
    [activeCategory, farmId, pendingItems]
  );

  const groupedSections = useMemo(() => {
    if (activeCategory !== "all") {
      return [];
    }
    return PENDING_ACTION_CATEGORIES.filter((category) => category.id !== "all")
      .map((category) => ({
        category,
        items: filterPendingByCategory(pendingItems, category.id, farmId),
      }))
      .filter((section) => section.items.length > 0);
  }, [activeCategory, farmId, pendingItems]);

  const activeCategoryMeta = resolvePendingCategory(activeCategory);
  const isLoading = loading || localLoading;

  return (
    <div className="field-app-pending-actions">
      <div className="field-app-pending-actions-head">
        <div>
          <h2>{t("Ações pendentes e alertas")}</h2>
          <p>
            {t(
              "Central de alertas da plataforma — encerramentos incompletos, leitura inconsistente de odômetro ou horímetro, odômetro ou horímetro sem variação, velocidade, perda de colheita, manutenção vencida e demais itens que precisam de atenção do gestor."
            )}
          </p>
          {activeCategory !== "all" ? (
            <p className="field-app-pending-actions-filter-note">
              {t("Filtrando")}: <strong>{t(activeCategoryMeta.label)}</strong>{" "}
              <Link to={`/${farmId}/field-app/?tab=pending-actions`}>
                {t("Ver todos")}
              </Link>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={reload}
          disabled={isLoading}
        >
          {t("Atualizar")}
        </button>
      </div>

      {isLoading ? (
        <div className="field-app-loading">
          <MDSpinner singleColor="#1ABB9C" size={40} />
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="field-app-pending-actions-empty">
          {activeCategory === "speed_violation" ? (
            <>
              <p>{t("Nenhum alerta de velocidade no momento.")}</p>
              <p className="text-muted">
                {t(
                  "O alerta é gerado ao encerrar um trabalho no app, comparando a velocidade GPS do turno com o limite cadastrado em Equipamentos → Máquinas (Velocidade máxima). Se a máquina ficou acima do limite por tempo relevante, o alerta aparece aqui."
                )}
              </p>
            </>
          ) : (
            <p>{t("Nenhuma ação pendente ou alerta no momento.")}</p>
          )}
        </div>
      ) : activeCategory === "all" ? (
        <div className="field-app-pending-actions-groups">
          {groupedSections.map((section) => (
            <PendingActionCategorySection
              key={section.category.id}
              category={section.category}
              items={section.items}
              farmId={farmId}
              t={t}
              onResolved={handleResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={handleEditingChange}
              defaultExpanded
            />
          ))}
        </div>
      ) : (
        <div className="field-app-pending-actions-list">
          {visibleItems.map((item) => (
            <PendingActionCard
              key={`${item.actionType}-${item.sessionUuid || item.recordUuid || item.maintenanceId}`}
              item={item}
              farmId={farmId}
              t={t}
              onResolved={handleResolved}
              onOpenInTracking={onOpenInTracking}
              onEditingChange={handleEditingChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
